import { useEffect } from 'react';

/** What is open on the Field Mapping screen, deepest layer first. */
export type EscapeState = {
  /** A confirmation is showing — "Switch to Circle?", "Discard Field Markings?", Add Field Details.
   *  antd modals swallow their own Escape, so in practice the resolver never sees this case; it is
   *  in the order to document intent (grilling Q18). */
  modalOpen: boolean;
  /** A Point or Mid Point tooltip is open (113600). */
  tooltipOpen: boolean;
  /** The coordinate panel's draft has been touched, so Escape means cancel-with-confirmation
   *  (113601). A clean draft is not an Escape target — Escape does nothing rather than closing a
   *  panel the Grower did not open by accident. */
  panelDirty: boolean;
};

export type EscapeTarget = 'modal' | 'tooltip' | 'panel-cancel' | 'none';

/**
 * What one Escape press means (grilling **Q18 / decision 21**).
 *
 * **Deepest first, and exactly one answer.** The rule exists because Escape on this screen has three
 * possible meanings at once, and a press that closed a tooltip AND cancelled the panel behind it is
 * the exact defect `<MapPage>`'s single-listener comment already warns about.
 *
 * Pure, so all four outcomes and the precedence between them are tested as arithmetic with no DOM —
 * which is what lets tickets 07, 09 and 11 add their state to this function instead of adding a
 * listener each. No `escape-order` module is introduced (spec.md § Screen and routing): this app has
 * one screen with an Escape order, and it is this one.
 */
export function resolveEscape({ modalOpen, tooltipOpen, panelDirty }: EscapeState): EscapeTarget {
  if (modalOpen) return 'modal';
  if (tooltipOpen) return 'tooltip';
  if (panelDirty) return 'panel-cancel';
  return 'none';
}

/**
 * The Field Mapping screen's **one** `keydown` listener.
 *
 * A hook rather than an effect in the page so the rule is greppable and enforceable: no component on
 * this screen adds its own (113600). `onEscape` is called with the resolved target and never with
 * `'none'`, so a caller cannot accidentally act on a press that meant nothing.
 */
export function useMappingEscape(
  state: EscapeState,
  onEscape: (target: Exclude<EscapeTarget, 'none'>) => void,
): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const target = resolveEscape(state);
      if (target !== 'none') onEscape(target);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state, onEscape]);
}
